'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'next-intl';
import { Trash2 } from 'lucide-react';
import type { Table } from '@/lib/table-features';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { SHELVES_HASH } from '../constants';
import { useDeleteShelf } from '../services/shelves.mutations';
import type { Shelf } from '../types';

interface ShelvesTableActionBarProps {
  table: Table<Shelf>;
}

export function ShelvesTableActionBar({ table }: ShelvesTableActionBarProps) {
  const t = useTranslations('shelves');
  const tc = useTranslations('common');
  const { open: bulkOpen, onOpenChange: toggleBulk } = useHashDialog(
    SHELVES_HASH.bulkDelete
  );

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const deleteShelf = useDeleteShelf();
  const isDeleting = deleteShelf.isPending;

  const handleBulkDelete = async () => {
    const ids = selectedRows.map((row) => row.original.id);
    try {
      for (const id of ids) {
        await deleteShelf.mutateAsync(id);
      }
      toggleBulk(false);
      table.resetRowSelection();
    } catch {
      // Error handling is done by the mutation meta
    }
  };

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('table.selectedCount', { count: selectedRows.length })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleBulk(true)}
            disabled={isDeleting}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('delete')}
          </Button>
        </div>
      </div>

      <AlertDialog open={bulkOpen} onOpenChange={toggleBulk}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex justify-end gap-3'>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className='bg-destructive hover:bg-destructive/90'
            >
              {isDeleting ? '...' : t('delete')}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
